import { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { colors, fonts, settings, zindex, animate } from '../styles/theme';
import { Dropdown } from '../containers';
import customIcons from '../styles/icons';
import { switcherHeight } from './theme-nav';

const readyStyle = css`
  * {
    filter: none;
  }
`;

const loadingStyle = css`
  overflow: hidden;
  > * {
    filter: blur(4px);
    pointer-events: none;
  }
`;

const SwitcherWrapper = styled('div')`
  background-color: ${colors.blue.primaryDark};
  position: relative;
  z-index: ${zindex.component.aboveBase};
  pointer-events: none;
  &:hover + nav > * {
    transition: opacity ${animate.transition} linear;
    opacity: 0.5;
  }
  ${(props) => (props.ready ? readyStyle : loadingStyle)};
`;

const SelectorWrapper = styled('div')`
  height: ${switcherHeight};
  display: flex;
  align-items: center;
  pointer-events: all;
  margin-right: 10px;
  &:hover {
    z-index: ${zindex.component.base};
    background: ${({ disabled }) => (disabled ? 'none' : colors.blue.primary)};
    cursor: pointer;
  }
  @media screen and (max-width: 640px) {
    margin-right: 0;
    width: 100%;
  }
`;

const SwitcherInnerWrapper = styled('div')`
  max-width: ${settings.page.maxWidth};
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: 0 auto;
  position: relative;
  width: 100%;
  color: ${colors.white};
`;

const SelectionLabel = styled('span')`
  font-size: 10px;
  text-transform: uppercase;
  color: ${colors.navBlue};
  padding-right: 10px;
  padding-left: 10px;
  pointer-events: none;
`;

const SelectionValue = styled('span')`
  padding-right: 10px;
  font-weight: ${fonts.sans.weight.bold};
  &::after {
    /* adds Selectordropdown arrow */
    ${fonts.icons}
    content: '${customIcons['angle-down']}';
    display: inline-block;
    line-height: 0;
    margin-left: 3px;
    position: relative;
    top: 1px;
    color: inherit;
  }
`;

const SelectionWrapper = styled('div')`
  line-height: ${switcherHeight};
  white-space: nowrap;
  display: flex;
`;

const disabledDropdownStyles = css`
  &:hover {
    ${SelectionWrapper} {
      background: none;
      color: ${colors.white};
      cursor: not-allowed;
    }
  }
  ${SelectionWrapper} {
    background: none;
    color: ${colors.white};
  }
  ${SelectionValue} {
    &:after {
      display: none;
    }
  }
`;

const SelectorDropdown = styled(Dropdown)`
  width: 100%;
  li {
    white-space: initial;
  }
  ${({ disabled }) => (disabled ? disabledDropdownStyles : '')};
`;

 
const Selection = ({ label, value }) => {
  return (
    <SelectionWrapper>
      <SelectionLabel>{label}</SelectionLabel>
      <SelectionValue>{value}</SelectionValue>
    </SelectionWrapper>
  );
};

Selection.defaultProps = {};

Selection.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

class AccountSelector extends Component {
  static buildOption(linkData, action) {
    return {
      name: '',
      id: null,
      children: [],
      action,
      ...linkData,
    };
  }

  get accountOptions() {
    const { accountOptions, selectedAccount } = this.props;
    return accountOptions
      .map((option) => AccountSelector.buildOption(option, this.onAccountSelect))
      .filter((option) => option.id !== selectedAccount.id);
  }

  get subAccountOptions() {
    const { selectedAccount, selectedSubAccount } = this.props;
    return selectedAccount.children
      .map((option) => AccountSelector.buildOption(option, this.onSubAccountSelect))
      .filter((option) => option.id !== selectedSubAccount.id);
  }

  onAccountSelect = (evt) => {
    const { onAccountChange, accountOptions } = this.props;
    const { text } = evt.currentTarget;
    const account = accountOptions.find((l) => l.name === text);

    evt.preventDefault();
    onAccountChange(account);
  };

  onSubAccountSelect = (evt) => {
    const { onSubAccountChange, selectedAccount } = this.props;
    const { text } = evt.currentTarget;
    const account = selectedAccount.children.find((l) => l.name === text);

    evt.preventDefault();
    onSubAccountChange(account);
  };

  render() {
    const { accountLabel, subAccountLabel, selectedAccount, selectedSubAccount, ready } =
      this.props;

    const otherEnabledAccounts = this.accountOptions.filter((account) => !account.disabled);
    const accountTriggerProps = {
      label: accountLabel,
      value: selectedAccount.name,
      disabled: !otherEnabledAccounts.length,
    };
    const subAccountTriggerProps = {
      label: subAccountLabel,
      value: selectedSubAccount.name,
      disabled: !this.subAccountOptions.length,
    };

    return (
      <SwitcherWrapper ready={ready}>
        <SwitcherInnerWrapper>
          {otherEnabledAccounts.length > 0 && (
            <SelectorWrapper>
              <SelectorDropdown
                Trigger={Selection}
                triggerProps={accountTriggerProps}
                links={otherEnabledAccounts}
              />
            </SelectorWrapper>
          )}
          {selectedSubAccount ? (
            <SelectorWrapper disabled={!this.subAccountOptions.length}>
              <SelectorDropdown
                Trigger={Selection}
                triggerProps={subAccountTriggerProps}
                links={this.subAccountOptions}
                disabled={!this.subAccountOptions.length}
              />
            </SelectorWrapper>
          ) : null}
        </SwitcherInnerWrapper>
      </SwitcherWrapper>
    );
  }
}

AccountSelector.defaultProps = {
  accountLabel: '',
  subAccountLabel: '',
  ready: true,
  onAccountChange: () => {},
  onSubAccountChange: () => {},
};

const optionShape = {
  name: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
};

AccountSelector.propTypes = {
  accountLabel: PropTypes.string,
  subAccountLabel: PropTypes.string,
  accountOptions: PropTypes.arrayOf(
    PropTypes.shape({
      ...optionShape,
      children: PropTypes.arrayOf(PropTypes.shape(optionShape)),
    }).isRequired
  ).isRequired,
  ready: PropTypes.bool,
  onAccountChange: PropTypes.func,
  onSubAccountChange: PropTypes.func,
  selectedAccount: PropTypes.shape(optionShape).isRequired,
  selectedSubAccount: PropTypes.shape(optionShape).isRequired,
};

export default AccountSelector;
